import { apiClient } from './api-client';
import type {
  TeamWithMembers,
  CreateTeamDto,
  UpdateTeamDto,
  TeamFilters,
  TeamsListResponse,
  TeamStats,
  AddAgentsDto,
  AttachPreview,
  TeamTicketStats,
} from '../types/team';

/**
 * tenant-service wraps every response with a global TransformInterceptor:
 *   { success: true, data: <payload>, meta: {...} }
 *
 * `unwrap` extracts the payload — but stays defensive: some legacy endpoints
 * (or local mock data) may return the payload directly without the envelope,
 * so we only peel a layer when the envelope is actually present.
 */
interface Envelope<T> {
  success?: boolean;
  data: T;
  meta?: unknown;
}

function unwrap<T>(body: unknown): T {
  if (
    body &&
    typeof body === 'object' &&
    'success' in (body as Record<string, unknown>) &&
    'data' in (body as Record<string, unknown>)
  ) {
    return (body as Envelope<T>).data;
  }
  return body as T;
}

class TeamsService {
  private readonly basePath = '/teams';

  async getTeams(filters?: TeamFilters): Promise<TeamsListResponse> {
    const params = new URLSearchParams();
    if (filters?.search) params.append('search', filters.search);
    if (filters?.pipelineId) params.append('pipelineId', filters.pipelineId);
    if (filters?.supervisorId) params.append('supervisorId', filters.supervisorId);
    if (filters?.parentTeamId) params.append('parentTeamId', filters.parentTeamId);
    if (typeof filters?.isActive === 'boolean') {
      params.append('isActive', String(filters.isActive));
    }
    if (filters?.page) params.append('page', String(filters.page));
    if (filters?.limit) params.append('limit', String(filters.limit));

    const qs = params.toString();
    const url = qs ? `${this.basePath}?${qs}` : this.basePath;
    const res = await apiClient.get<Envelope<TeamsListResponse> | TeamsListResponse>(url);
    return unwrap<TeamsListResponse>(res.data);
  }

  async getTeam(id: string): Promise<TeamWithMembers> {
    const res = await apiClient.get<Envelope<TeamWithMembers> | TeamWithMembers>(
      `${this.basePath}/${id}`,
    );
    return unwrap<TeamWithMembers>(res.data);
  }

  async createTeam(dto: CreateTeamDto): Promise<TeamWithMembers> {
    const res = await apiClient.post<Envelope<TeamWithMembers> | TeamWithMembers>(
      this.basePath,
      dto,
    );
    return unwrap<TeamWithMembers>(res.data);
  }

  async updateTeam(id: string, dto: UpdateTeamDto): Promise<TeamWithMembers> {
    const res = await apiClient.put<Envelope<TeamWithMembers> | TeamWithMembers>(
      `${this.basePath}/${id}`,
      dto,
    );
    return unwrap<TeamWithMembers>(res.data);
  }

  async deleteTeam(id: string): Promise<void> {
    await apiClient.delete(`${this.basePath}/${id}`);
  }

  async setSupervisor(id: string, supervisorId: string): Promise<TeamWithMembers> {
    const res = await apiClient.put<Envelope<TeamWithMembers> | TeamWithMembers>(
      `${this.basePath}/${id}/supervisor`,
      { supervisorId },
    );
    return unwrap<TeamWithMembers>(res.data);
  }

  async addAgents(id: string, dto: AddAgentsDto): Promise<TeamWithMembers> {
    const res = await apiClient.post<Envelope<TeamWithMembers> | TeamWithMembers>(
      `${this.basePath}/${id}/members`,
      dto,
    );
    return unwrap<TeamWithMembers>(res.data);
  }

  async removeAgent(id: string, agentId: string): Promise<void> {
    await apiClient.delete(`${this.basePath}/${id}/members/${agentId}`);
  }

  /**
   * What putting team `id` under `parentTeamId` (or, with null, back to the
   * top level) would do. Changes nothing.
   */
  async getAttachPreview(id: string, parentTeamId: string | null): Promise<AttachPreview> {
    const qs = parentTeamId ? `?parentTeamId=${encodeURIComponent(parentTeamId)}` : '';
    const res = await apiClient.get<Envelope<AttachPreview> | AttachPreview>(
      `${this.basePath}/${id}/attach-preview${qs}`,
    );
    return unwrap<AttachPreview>(res.data);
  }

  /**
   * Ticket counts for every team in one call: open, created today, SLA
   * breached and waiting in its own queue. A parent includes its sub-teams.
   */
  async getTicketStats(): Promise<TeamTicketStats[]> {
    const res = await apiClient.get<{ data: TeamTicketStats[] } | Envelope<{ data: TeamTicketStats[] }>>(
      `${this.basePath}/stats`,
    );
    const body = unwrap<{ data: TeamTicketStats[] }>(res.data);
    return body?.data ?? [];
  }

  async getStats(id: string): Promise<TeamStats> {
    const res = await apiClient.get<Envelope<TeamStats> | TeamStats>(
      `${this.basePath}/${id}/stats`,
    );
    return unwrap<TeamStats>(res.data);
  }
}

export const teamsService = new TeamsService();
